import type { SetStateAction } from "react";
import { create } from "zustand/react";

import { randomUUID } from "../utils/uuid";
import { reconcileHistoryState } from "./reconcile-history-state";

const PREVIOUS_ENTRY_KEY = "historyTracker_previousId";
const CURRENT_ENTRY_KEY = "historyTracker_nextId";
const ENTRY_STATE_KEY = "historyTracker_state";

export interface HistoryTrackerEntry {
  id: string;
  previousId: string | null;
  state: Record<string, unknown> | null;
  url: string;
}

export interface HistoryTrackerState {
  currentEntryId: string | null;
  previousEntryId: string | null;
  state: Record<string, unknown> | null;
  url: string;

  entries: HistoryTrackerEntry[];

  reflectStateInHistory: () => void;
  registerNavigation: () => void;

  peekState: () => Record<string, unknown>;
  setState: (stateOrFn: SetStateAction<Record<string, unknown>>) => void;
  pushState: (stateOrFn: SetStateAction<Record<string, unknown>>) => void;

  calculateDistanceToState: (matcher: (state: Record<string, unknown> | null) => boolean) => number | null;
}

export const useHistoryTrackerStore = create<HistoryTrackerState>((set, get) => ({
  currentEntryId: null,
  previousEntryId: null,
  state: null,
  url: "",
  entries: [],

  reflectStateInHistory: () => {
    window.history.replaceState(
      {
        ...window.history.state,
        [PREVIOUS_ENTRY_KEY]: get().previousEntryId,
        [CURRENT_ENTRY_KEY]: get().currentEntryId,
        [ENTRY_STATE_KEY]: get().state,
      },
      "",
    );
  },
  registerNavigation: () => {
    const currentEntryId = get().currentEntryId;

    const historyState = window.history.state ?? {};

    const newPreviousEntryId = (historyState[PREVIOUS_ENTRY_KEY] ?? null) as string | null;
    const newCurrentEntryId = (historyState[CURRENT_ENTRY_KEY] ?? null) as string | null;
    const newUrl = window.location.href;
    const newState = (historyState[ENTRY_STATE_KEY] ?? null) as Record<string, unknown> | null;

    const reconciledState = reconcileHistoryState({
      currentEntryId,
      entries: get().entries,
      url: get().url,
      newCurrentEntryId,
      newPreviousEntryId,
      newUrl,
      newState,
    });

    if (reconciledState) {
      set(reconciledState);
      get().reflectStateInHistory();
    }
  },

  peekState: () => get().state ?? {},
  setState: (stateOrFn) => {
    const id = get().currentEntryId;

    const state = stateOrFn instanceof Function ? stateOrFn(get().state ?? {}) : stateOrFn;

    const newState = {
      ...(get().state ?? {}),
      ...(state ?? {}),
    };

    set({
      state: newState,
      entries: get().entries.map((entry) => (entry.id === id ? { ...entry, state: newState } : entry)),
    });

    get().reflectStateInHistory();
  },
  pushState: (stateOrFn) => {
    const state = stateOrFn instanceof Function ? stateOrFn(get().state ?? {}) : stateOrFn;

    const newState = {
      ...(get().state ?? {}),
      ...(state ?? {}),
    };
    const previousId = get().currentEntryId;
    const newId = randomUUID();

    set({
      currentEntryId: newId,
      previousEntryId: previousId,
      state: newState,
      entries: [
        ...get().entries,
        {
          id: newId,
          previousId: previousId,
          state: newState,
          url: get().url,
        },
      ],
    });

    window.history.pushState(
      {
        ...window.history.state,
        [PREVIOUS_ENTRY_KEY]: previousId,
        [CURRENT_ENTRY_KEY]: newId,
        [ENTRY_STATE_KEY]: newState,
      },
      "",
    );
  },

  calculateDistanceToState: (matcher) => {
    const currentState = get().state;
    if (matcher(currentState)) {
      return 0;
    }

    const entries = get().entries;

    let previousId = get().previousEntryId;
    if (!previousId) {
      return null;
    }

    let stepCount = 1;

    while (true) {
      const entry = entries.find(({ id }) => id === previousId);
      if (!entry) {
        return null;
      }
      if (matcher(entry.state)) {
        return stepCount;
      }

      if (!entry.previousId) {
        return null;
      }

      previousId = entry.previousId;
      stepCount++;
    }
  },
}));
